import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();

  return (
    <Container maxWidth="md" sx={{ py: 4, minHeight: "100vh" }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: theme.shadows[4],
        }}
      >
        {/* Header Section */}
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Last updated: February 2025
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Information We Collect */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">1. Information We Collect</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              We only collect contact information when users voluntarily interact with us. We do not create user accounts,
              store personal health data, or collect sensitive financial information.
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Full Name (when submitting inquiries or volunteering)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Email Address (for communication and confirmations)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Phone Number (for follow-ups and volunteer coordination)" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* How We Use Your Information */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">2. How We Use Your Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              We only use your contact details for:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Processing volunteer applications and communicating next steps." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Responding to user inquiries related to donations, patient services, and events." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Sending confirmation emails for donations." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Providing updates on our mission, events, or related services." />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Data Security */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">3. Data Security & Storage</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              We prioritize data security and comply with HIPAA standards to protect your information.
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="HIPAA-Compliant Storage: Contact details are securely stored in AWS DynamoDB and S3." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Encryption: All data is encrypted during transmission and storage." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Access Control: Only authorized personnel can access your contact details." />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Donations and Payment Security */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">4. Donations & Payment Security</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              We use <Link href="https://stripe.com/" target="_blank" rel="noopener" underline="hover">Stripe</Link> to process donations securely.
              Hope Beyond International does not store any credit card details or financial information.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Data Retention */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">5. Data Retention & Deletion</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              We retain your data only for as long as necessary:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Volunteer & Inquiry Data: Retained until the request is resolved." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Donation Records: Retained for tax and compliance purposes." />
              </ListItem>
              <ListItem>
                <ListItemText primary="Service-Related Communications: Kept only for relevant follow-ups." />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Policy Updates */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "&:hover": { backgroundColor: "rgba(211, 211, 211, 0.3)" }, // Light gray hover effect
            }}
          >
            <Typography variant="h5">6. Changes to This Privacy Policy</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              We may update this Privacy Policy periodically. Continued use of our services implies acceptance of any updates.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="textSecondary" textAlign="center">
          If you have any questions, please contact us at{" "}
          <Link href="mailto:info@hbinternational.org" underline="hover">
            info@hbinternational.org
          </Link>.
        </Typography>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
